/**
 * DocumentBody — the pane's rendering of the canonical document.
 *
 * It walks the SAME `ResearchDocument` the .docx and the clipboard are built
 * from (`documentModel.buildDocument`), so preview and export cannot diverge:
 * one block list, two serialisations.
 *
 * There is no chrome here on purpose — headings, paragraphs and whitespace.
 * The document arrives in the session's output language, so this file adds no
 * text of its own: every string on screen comes from the payload.
 */

import React from 'react';
import { ResearchCitation } from '../types';
import { DocBlock, ResearchDocument } from '../documentModel';
import MarkdownStream from '../../Chat/MarkdownStream';

interface Props {
  doc: ResearchDocument;
  citations: ResearchCitation[];
  onOpenCitation: (target: string) => void;
}

/** Superscript markers pointing into the footnote list. */
const Markers: React.FC<{
  numbers?: number[];
  doc: ResearchDocument;
  onOpenCitation: (target: string) => void;
}> = ({ numbers, doc, onOpenCitation }) => {
  if (!numbers || numbers.length === 0) return null;
  return (
    <span className="whitespace-nowrap" data-testid="footnote-markers">
      {numbers.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onOpenCitation(doc.footnotes[n - 1]?.target || String(n))}
          data-testid="footnote-marker"
          title={doc.footnotes[n - 1]?.label}
          className="align-super text-[10px] font-medium ml-0.5 text-champagne
                     hover:underline transition-colors duration-200 ease-slide"
        >
          {n}
        </button>
      ))}
    </span>
  );
};

const Block: React.FC<{
  block: DocBlock;
  doc: ResearchDocument;
  citations: ResearchCitation[];
  onOpenCitation: (target: string) => void;
}> = ({ block, doc, citations, onOpenCitation }) => {
  const markers = (numbers?: number[]) => (
    <Markers numbers={numbers} doc={doc} onOpenCitation={onOpenCitation} />
  );

  switch (block.kind) {
    case 'heading':
      return (
        <h2
          className="text-[15px] font-semibold text-fg-strong mt-7 mb-2 first:mt-0"
          data-testid="doc-heading"
        >
          {block.text}
        </h2>
      );

    case 'paragraph':
      return (
        <p
          className={
            block.lead
              ? 'text-[15px] leading-7 text-fg-body mb-4'
              : 'text-[14px] leading-7 text-fg-body mb-4'
          }
          data-testid="doc-paragraph"
        >
          {block.text}
          {markers(block.footnotes)}
        </p>
      );

    case 'list':
      return (
        <ul className="mb-4 space-y-1.5" data-testid="doc-list">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-[14px] leading-7 text-fg-body">
              <span className="mt-[11px] w-1 h-1 rounded-full bg-champagne flex-shrink-0" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case 'markdown':
      // The draft body keeps the shared markdown path: clause headings,
      // placeholder marks and inline [N] markers all behave as elsewhere.
      return (
        <div className="mb-4" data-testid="doc-markdown">
          <MarkdownStream
            text={block.text}
            citations={citations}
            isLive={false}
            onComplete={() => {}}
            onOpenCitation={onOpenCitation}
          />
        </div>
      );

    case 'event':
      return (
        <div className="mb-5" data-testid="doc-event">
          <p className="text-[14px] leading-7 text-fg-body">
            {block.date && (
              <span className="font-semibold text-fg-strong">
                {block.date}
                {block.approximate && <span className="text-fg-muted"> ~</span>}
              </span>
            )}
            {block.date && block.text ? ' — ' : ''}
            {block.text}
            {markers(block.footnotes)}
          </p>
          {block.actors && block.actors.length > 0 && (
            <p className="text-[12px] text-fg-muted">{block.actors.join(', ')}</p>
          )}
          {block.significance && (
            <p className="text-[13px] leading-6 text-fg-muted">{block.significance}</p>
          )}
        </div>
      );

    default:
      return null;
  }
};

const DocumentBody: React.FC<Props> = ({ doc, citations, onOpenCitation }) => (
  <article
    className="max-w-[68ch] mx-auto text-fg-body"
    data-testid="artifact-document"
  >
    {doc.title && (
      <h1 className="text-lg font-semibold text-fg-strong mb-4" data-testid="doc-title">
        {doc.title}
      </h1>
    )}

    {doc.blocks.map((block, i) => (
      <Block
        key={i}
        block={block}
        doc={doc}
        citations={citations}
        onOpenCitation={onOpenCitation}
      />
    ))}

    {/* Footnotes. Numbered, after a rule — a label here would be our English
        inside a document that is not in English. */}
    {doc.footnotes.length > 0 && (
      <ol className="mt-8 pt-4 border-t border-hairline-soft space-y-1.5"
          data-testid="doc-footnotes">
        {doc.footnotes.map((note, i) => (
          <li key={note.label} className="flex gap-2 text-[12px] leading-6">
            <span className="text-fg-muted tabular-nums flex-shrink-0">{i + 1}.</span>
            <span className="min-w-0">
              <button
                type="button"
                onClick={() => onOpenCitation(note.target)}
                data-testid="footnote-entry"
                className="font-medium text-fg-body hover:text-champagne text-left
                           transition-colors duration-200 ease-slide"
              >
                {note.label}
              </button>
              {note.detail && <span className="text-fg-muted"> — {note.detail}</span>}
            </span>
          </li>
        ))}
      </ol>
    )}
  </article>
);

export default DocumentBody;
